import {PostgrestError} from '@supabase/supabase-js';
import {QueryFunction, QueryKey, UseQueryOptions, useQuery} from '@tanstack/react-query';

import {getQueryKey} from '../lib/helpers/queryKeys';
import {convertColumnsToString} from '../lib/helpers/string';
import {supabase} from '../lib/utils/supabaseClass';
import {sortOptionT} from '../types/dataTable.types';
import {Database} from '../types/database.types';
import {limitT} from '../types/pagination.types';
import {
  BaseFilterT,
  ColumnSelectionT,
  CombinedColumnsT,
  DotNotationPathT,
  FilterT,
  NotFilterT,
  OrFilterT,
  PaginationKeyT,
  SelectedColumnsKeysT,
} from '../types/query.types';
import {AllTablesT, FlattenT} from '../types/utility.types';

/*******************************************************************
 * Types
 *******************************************************************/
// Type for the response of the useDataQuery hook
export type UseDataQueryResponseT<
  T extends keyof AllTablesT,
  C extends ColumnSelectionT<T>[],
  R extends CombinedColumnsT<T, C> = CombinedColumnsT<T, C>,
> = {
  data: R[];
  total: number;
  currentPage: number;
  totalPages: number;
};

// Type for the props of the useDataQuery hook
export type UseDataQueryPropsT<
  T extends keyof Database['public']['Tables'],
  C extends ColumnSelectionT<T>[] = ['*'],
  TData = UseDataQueryResponseT<T, C>,
  TError extends Error = Error,
  TQueryFnData = UseDataQueryResponseT<T, C>,
  TQueryKey extends QueryKey = QueryKey,
> = FlattenT<
  Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey' | 'queryFn' | 'queryHash' | 'queryKeyHashFn'
  > & {
    table: T;
    columns?: C;
    enabled?: boolean;
    page?: number;
    limit?: limitT;
    sortOption?: sortOptionT<T>;
    searchTerm?: string;
    searchColumn?: SelectedColumnsKeysT<T, C>;
    all?: boolean;
    filters?: FilterT<T, C>[];
    protect?: boolean;
    authOverride?: boolean;
  }
>;

// Type for the props of the fetchData function
type FetchDataPropsT<T extends keyof AllTablesT, C extends ColumnSelectionT<T>[]> = {
  table: T;
  columns?: C;
  sortDirection?: 'asc' | 'desc';
  page?: number | null;
  limit?: limitT | null;
  searchTerm?: string;
  searchColumn?: SelectedColumnsKeysT<T, C>;
  sortColumn?: keyof Database['public']['Tables'][T]['Row'];
  all?: boolean;
  enabled?: boolean;
  filters?: FilterT<T, C>[];
};

/*********************************************************************
 * Helper Functions
 *********************************************************************/
// Function to validate the paginationKey
function isPaginationKey(value: string): value is PaginationKeyT {
  return value === 'all' || /^page:\d+ limit:\d+$/.test(value);
}

// Add this helper function at the top with other helper functions
function handleDotNotation<T extends keyof Database['public']['Tables'], C extends ColumnSelectionT<T>[]>(
  column: DotNotationPathT<CombinedColumnsT<T, C>>
) {
  if (column.includes('.')) {
    const [table, field] = column.split('.');
    return `${table}->>${field}`;
  }
  return column;
}

// Fetches data from the database
async function fetchData<
  T extends keyof AllTablesT,
  C extends ColumnSelectionT<T>[],
  R extends CombinedColumnsT<T, C> = CombinedColumnsT<T, C>,
>({
  table,
  columns = ['*'] as C,
  sortDirection = 'desc',
  page = null,
  limit = null,
  searchTerm = '',
  searchColumn = `${String(table).toLowerCase()}_name` as SelectedColumnsKeysT<T, C>,
  sortColumn = 'date_created' as keyof Database['public']['Tables'][T]['Row'],
  all = !page && !limit,
  filters = [],
}: FetchDataPropsT<T, C>) {
  let query = supabase
    .from(table as keyof Database['public']['Tables'])
    .select<string, R>(convertColumnsToString(columns), {count: 'exact'})
    .throwOnError();

  // Apply search if searchTerm is provided
  if (searchTerm) {
    query = query.ilike(String(searchColumn), `%${searchTerm}%`);
  }

  // Apply filters
  filters.forEach(filter => {
    // Type guard to determine the type of filter
    function isBaseFilter(filter: FilterT<T, C>): filter is BaseFilterT<T, C> {
      return 'column' in filter && 'operator' in filter && 'value' in filter;
    }

    function isNotFilter(filter: FilterT<T, C>): filter is NotFilterT<T, C> {
      return 'not' in filter;
    }

    function isOrFilter(filter: FilterT<T, C>): filter is OrFilterT<T, C> {
      return 'or' in filter;
    }

    // Handle different filter types
    if (isNotFilter(filter) && filter.not) {
      const {column, operator, value} = filter.not;
      if (!column || !operator) throw new Error('Column is required for NOT filter');

      query = query.not(String(column), operator, value);
    } else if (isOrFilter(filter) && filter.or) {
      const orConditions = filter.or
        .map(orFilter => {
          const {column: orColumn, operator: orOperator, value: orValue} = orFilter;
          if (!orColumn || !orOperator) throw new Error('Column, operator, and value are required for OR filter');
          return `${String(orColumn)} ${orOperator} ${orValue}`;
        })
        .join(' OR ');
      query = query.or(orConditions);
    } else if (isBaseFilter(filter)) {
      const {column, operator, value} = filter;
      if (!column || !operator) throw new Error('Column, operator, and value are required for filter');

      const processedColumn = String(handleDotNotation<T, C>(column));

      switch (operator) {
        case 'eq':
          if (value === null || value === undefined)
            throw new Error('Value cannot be null or undefined for EQ operator, use IS operator instead');
          query = query.eq(processedColumn, value);
          break;
        case 'neq':
          query = query.neq(processedColumn, value);
          break;
        case 'gt':
          query = query.gt(processedColumn, value);
          break;
        case 'gte':
          query = query.gte(processedColumn, value);
          break;
        case 'lt':
          query = query.lt(processedColumn, value);
          break;
        case 'lte':
          query = query.lte(processedColumn, value);
          break;
        case 'like':
          query = query.like(processedColumn, String(value));
          break;
        case 'ilike':
          query = query.ilike(processedColumn, String(value));
          break;
        case 'in':
          if (!Array.isArray(value)) throw new Error('Value must be an array for IN operator');
          query = query.in(processedColumn, value);
          break;
        case 'is':
          if (value === null || typeof value === 'boolean') {
            query = query.is(processedColumn, value);
          } else {
            throw new Error('Value must be null or boolean for IS operator, use EQ operator for other types of values');
          }
          break;
        default:
          throw new Error(`Unsupported operator: ${operator}`);
      }
    } else {
      throw new Error('Invalid filter');
    }
  });

  // Apply sorting
  query = query.order(String(sortColumn), {ascending: sortDirection === 'asc'});

  // Apply pagination if not fetching all
  if (!all) {
    if (page !== null && limit !== null) {
      query = query.range((page - 1) * limit, page * limit - 1);
    }
  }

  const {data, error, count} = await query;

  if (error) {
    throw new Error(`Error fetching ${String(table)}: ${(error as PostgrestError).message}`);
  }

  const totalPages = limit ? Math.ceil((count ?? 0) / limit) : 1;

  const response: UseDataQueryResponseT<T, C> = {
    data: data,
    total: count ?? 0,
    totalPages: totalPages ?? 1,
    currentPage: page ?? 1,
  };

  return response;
}

/*********************************************************************
 * useDataQuery Hook
 * Wrapper around useQuery to fetch data from the database
 *********************************************************************/
export const useDataQuery = <T extends keyof Database['public']['Tables'], C extends ColumnSelectionT<T>[]>({
  table,
  columns = ['*'] as C,
  enabled = true,
  page = undefined,
  limit = undefined,
  all = (!page && !limit) ?? true,
  sortOption = {column: 'date_created', direction: 'desc', label: 'Date Created'} as sortOptionT<T>,
  searchColumn = `${String(table).toLowerCase()}_name` as SelectedColumnsKeysT<T, C>,
  searchTerm = '',
  filters = [],
  ...rest
}: UseDataQueryPropsT<T, C>): ReturnType<
  typeof useQuery<UseDataQueryResponseT<T, C>, Error, UseDataQueryResponseT<T, C>, QueryKey>
> & {
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
  };
} => {
  const paginationKey = page !== undefined && limit !== undefined ? `page:${page} limit:${limit}` : 'all';
  if (!isPaginationKey(paginationKey)) throw new Error('Invalid pagination key');

  const queryKey = all
    ? getQueryKey.all(table, {columns, enabled, filters, sortOption, searchTerm, searchColumn})
    : getQueryKey.list(table, {
        columns,
        enabled,
        filters,
        sortOption,
        page: page ?? 1,
        limit: limit ?? 5,
        searchTerm,
        searchColumn,
      });

  const queryFn: QueryFunction<UseDataQueryResponseT<T, C>, QueryKey> = async () => {
    return fetchData<T, C>({
      table,
      sortColumn: sortOption.column,
      sortDirection: sortOption.direction,
      page: page ?? null,
      limit: limit ?? null,
      columns,
      enabled,
      searchTerm,
      searchColumn,
      all,
      filters,
    });
  };

  // Options for the query
  const queryOptions: UseQueryOptions<UseDataQueryResponseT<T, C>, Error, UseDataQueryResponseT<T, C>, QueryKey> = {
    enabled,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retryOnMount: false,
    ...rest,
    queryKey,
    queryFn,
  };

  const queryResult = useQuery(queryOptions);

  return {
    ...queryResult,
    pagination: {
      currentPage: queryResult.data?.currentPage ?? 1,
      totalPages: queryResult.data?.totalPages ?? 1,
      totalItems: queryResult.data?.total ?? 0,
    },
  };
};
